import styled from '@emotion/styled';
import { createElement } from 'react';

import { IconButton, Link, type LinkProps, Typography } from '@/shared/design-system';
import { forMobile, hoverable } from '@/shared/design-system';

type ExternalLinkProps = Extract<LinkProps, { href: string }>;

const ExternalLink = (props: ExternalLinkProps) => createElement(Link, props);

export const SetupContent = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: 510,
  minHeight: 0,
  boxSizing: 'border-box',
  overflowY: 'auto',
  overscrollBehavior: 'contain',

  [forMobile]: {
    height: 'auto',
    maxHeight: 'calc(100dvh - 120px)',
    paddingInline: theme.spacing[8],
    paddingBottom: theme.spacing[8],
  },
}));

export const StepList = styled('ol')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[24],
  padding: theme.spacing[0],
  margin: theme.spacing[0],
  listStyle: 'none',
}));

export const Step = styled('li')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '32px minmax(0, 1fr)',
  alignItems: 'start',
  gap: theme.spacing[16],

  [forMobile]: {
    gap: theme.spacing[8],
  },
}));

export const StepMarker = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  width: 32,
  height: 32,
  border: `1px solid ${theme.colors.border.default}`,
  borderRadius: theme.radius.full,
  color: theme.colors.text.secondary,
}));

export const StepBody = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[16],
  minWidth: 0,
  paddingTop: theme.spacing[2],
}));

export const StoreLinks = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing[8],
}));

export const StoreBadgeLink = styled(ExternalLink)(({ theme }) => ({
  ...hoverable(theme, 'lift'),
  display: 'inline-flex',
  width: 118,
  height: 40,
  padding: theme.spacing[0],
  borderRadius: theme.radius[8],
  flexShrink: 0,

  svg: {
    display: 'block',
    width: 118,
    height: 40,
  },
}));

export const QrCard = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  boxSizing: 'border-box',
  padding: theme.spacing[16],
  gap: theme.spacing[24],
  border: `1px solid ${theme.colors.border.default}`,
  borderRadius: theme.radius[16],
  backgroundColor: theme.colors.background.canvas,

  [forMobile]: {
    alignItems: 'flex-start',
    flexDirection: 'column',
    gap: theme.spacing[16],
  },
}));

export const QrCodeFrame = styled('div')(({ theme }) => ({
  width: 120,
  height: 120,
  padding: theme.spacing[0],
  backgroundColor: theme.colors.background.canvas,
  flexShrink: 0,
}));

export const ManualKeyContent = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[8],
  minWidth: 0,
  flex: 1,
}));

export const ManualKeyRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing[8],
  minWidth: 0,
}));

export const ManualKey = styled(Typography)({
  minWidth: 0,
  overflowWrap: 'anywhere',
  flex: 1,
});

export const CopyButton = styled(IconButton)(({ theme }) => ({
  ...hoverable(theme),
  flexShrink: 0,

  [forMobile]: {
    minWidth: 44,
    minHeight: 44,
  },
}));

export const OtpBlock = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing[8],
  minWidth: 0,
}));

export const PendingStatus = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing[8],
  color: theme.colors.text.secondary,
}));

export const StateSurface = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  minHeight: 240,
  padding: theme.spacing[24],
  boxSizing: 'border-box',
  border: `1px solid ${theme.colors.border.default}`,
  borderRadius: theme.radius[12],
  backgroundColor: theme.colors.background.canvas,
}));

export const StateContent = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing[16],
  maxWidth: 360,
  textAlign: 'center',
}));

export const LiveRegion = styled('span')({
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  border: 0,
});
